import {
    dateOffset,
    dateStringToLocalDate,
    getDateString,
    getQuarterEndMonth,
    getQuarterNumber,
    getQuarterStartMonth,
} from 'app/core/helpers/date';
import moment from 'moment';

export interface DateRangeValue {
    start: string;
    end: string;
}

export enum DefaultDateRange {
    today = 'today',
    yesterday = 'yesterday',
    last7Days = 'last7Days',
    last14Days = 'last14Days',
    last30Days = 'last30Days',
    last60Days = 'last60Days',
    last90Days = 'last90Days',
    lastWeek = 'lastWeek',
    lastMonth = 'lastMonth',
    lastQuarter = 'lastQuarter',
    lastYear = 'lastYear',
    thisWeek = 'thisWeek',
    thisMonth = 'thisMonth',
    thisQuarter = 'thisQuarter',
    thisYear = 'thisYear',
    allTime = 'allTime',
}

export interface CustomDateRange {
    label: string;
    style?: {
        [property: string]: string;
    };
    cssClasses?: Array<string>;
    getValue(...args: Array<any>): DateRangeValue;
}

export class DateRange implements DateRangeValue {
    start: string = null;
    end: string = null;
    private values: Array<string> = [];

    static compareDates(a: Date | string, b: Date | string): number {
        return new Date(a).getTime() - new Date(b).getTime();
    }

    static createRangeValue(type: DefaultDateRange): DateRangeValue {
        let today = new Date();

        switch (type) {
            case DefaultDateRange.today:
                return {
                    start: getDateString(today),
                    end: getDateString(today),
                };
            case DefaultDateRange.yesterday:
                let yesterday = dateOffset(today, {days: -1});
                return {
                    start: getDateString(yesterday),
                    end: getDateString(yesterday),
                };
            case DefaultDateRange.last7Days:
                let sevenDaysAgo = dateOffset(today, {days: -6});
                return {
                    start: getDateString(sevenDaysAgo),
                    end: getDateString(today),
                };
            case DefaultDateRange.last14Days:
                let fourteenDaysAgo = dateOffset(today, {days: -13});
                return {
                    start: getDateString(fourteenDaysAgo),
                    end: getDateString(today),
                };
            case DefaultDateRange.last30Days:
                let thirtyDaysAgo = dateOffset(today, {days: -29});
                return {
                    start: getDateString(thirtyDaysAgo),
                    end: getDateString(today),
                };
            case DefaultDateRange.last60Days:
                let sixtyDaysAgo = dateOffset(today, {days: -59});
                return {
                    start: getDateString(sixtyDaysAgo),
                    end: getDateString(today),
                };
            case DefaultDateRange.last90Days:
                let ninetyDaysAgo = dateOffset(today, {days: -89});
                return {
                    start: getDateString(ninetyDaysAgo),
                    end: getDateString(today),
                };
            case DefaultDateRange.thisWeek:
                let weekStart = moment().startOf('week').toDate();
                let weekEnd = moment().endOf('week').toDate();
                return {
                    start: getDateString(weekStart),
                    end: getDateString(weekEnd),
                };
            case DefaultDateRange.lastWeek:
                let lastWeekStart = moment().subtract(1, 'weeks').startOf('week').toDate();
                let lastWeekEnd = moment().subtract(1, 'weeks').endOf('week').toDate();
                return {
                    start: getDateString(lastWeekStart),
                    end: getDateString(lastWeekEnd),
                };
            case DefaultDateRange.thisMonth:
                let monthStart = dateOffset(today, {days: 1 - today.getUTCDate()});
                let monthEnd = dateOffset(today, {days: -today.getUTCDate(), months: 1});
                return {
                    start: getDateString(monthStart),
                    end: getDateString(monthEnd),
                };
            case DefaultDateRange.lastMonth:
                let lastMonthStart = dateOffset(today, {days: 1 - today.getUTCDate(), months: -1});
                let lastMonthEnd = dateOffset(today, {days: -today.getUTCDate()});
                return {
                    start: getDateString(lastMonthStart),
                    end: getDateString(lastMonthEnd),
                };
            case DefaultDateRange.thisQuarter:
                let quarterNumber = getQuarterNumber(today);
                let quarterStart = new Date(today);
                let quarterEnd = new Date(today);
                quarterStart.setUTCMonth(getQuarterStartMonth(quarterNumber));
                quarterStart.setUTCDate(1);
                quarterEnd.setUTCMonth(getQuarterEndMonth(quarterNumber) + 1);
                quarterEnd.setUTCDate(0);
                return {
                    start: getDateString(quarterStart),
                    end: getDateString(quarterEnd),
                };
            case DefaultDateRange.lastQuarter:
                let lastQuarterNumber = (getQuarterNumber(today) - 1 || 4) as 1 | 2 | 3 | 4;
                let lastQuarterStart = new Date(today);
                let lastQuarterEnd = new Date(today);
                if (lastQuarterNumber === 4) {
                    lastQuarterStart.setFullYear(lastQuarterStart.getUTCFullYear() - 1);
                    lastQuarterEnd.setFullYear(lastQuarterEnd.getUTCFullYear() - 1);
                }
                lastQuarterStart.setMonth(getQuarterStartMonth(lastQuarterNumber));
                lastQuarterStart.setUTCDate(1);
                lastQuarterEnd.setUTCMonth(getQuarterEndMonth(lastQuarterNumber) + 1);
                lastQuarterEnd.setUTCDate(0);
                return {
                    start: getDateString(lastQuarterStart),
                    end: getDateString(lastQuarterEnd),
                };
            case DefaultDateRange.thisYear:
                let thisYearStart = moment().subtract(0, 'year').startOf('year').toDate();
                let thisYearEnd = moment().toDate();
                return {
                    start: getDateString(thisYearStart),
                    end: getDateString(thisYearEnd),
                };
            case DefaultDateRange.lastYear:
                let lastYearStart = moment().subtract(1, 'year').startOf('year').toDate();
                let lastYearEnd = moment().subtract(1, 'year').endOf('year').toDate();
                return {
                    start: getDateString(lastYearStart),
                    end: getDateString(lastYearEnd),
                };
            case DefaultDateRange.allTime:
                let allTimeStart = moment('2015-01-01').toDate();
                let allTimeEnd = moment().toDate();
                return {
                    start: getDateString(allTimeStart),
                    end: getDateString(allTimeEnd),
                };
        }
    }

    static createDateRangeDto({start, end}: DateRangeValue) {
        let startDateObj = dateStringToLocalDate(start, 0, 0, 0, 0);
        /* eslint-disable @typescript-eslint/no-magic-numbers */
        let endDateObj = dateStringToLocalDate(end, 23, 59, 59, 999);
        /* eslint-enable no-magic-numbers */
        return {
            start: startDateObj.toISOString(),
            end: endDateObj.toISOString(),
        };
    }

    constructor(value?: DateRangeValue) {
        if (value) {
            this.start = value.start;
            this.end = value.end;
            if (value.start) {
                this.values.push(value.start);
            }
            if (value.end) {
                this.values.push(value.end);
            }
        }
    }

    /**
     * Adds value to range. Range is full only if two values were added.
     *
     * @param value    date string on format yyyy-mm-dd
     */
    addValue(value: string) {
        if (this.isFull()) {
            this.clear();
        }
        this.values.push(value);
        this.assignValues();
    }

    /**
     * Returns true if one of the range limits are specified
     *
     * @return
     */
    hasValue(): boolean {
        return !!this.values.length;
    }

    /**
     * Returns true if start and end of the range are specified
     *
     * @return
     */
    isFull(): boolean {
        return this.values.length === 2;
    }

    /**
     * Clears range
     */
    clear() {
        this.start = null;
        this.end = null;
        this.values.splice(0, 2);
    }

    /**
     * Returns object with start and end values of the range
     *
     * @return
     */
    getRange(): DateRangeValue {
        return {
            start: this.start,
            end: this.end,
        };
    }

    getTempValue(): DateRangeValue {
        let [start, end] = this.values.slice().sort(DateRange.compareDates);
        return {
            start: start || '',
            end: end || '',
        };
    }

    /**
     * Returns true if passed date string is one of range limits
     *
     * @param value    date string in format yyyy-mm-dd
     */
    isRangeLimit(value: string): boolean {
        return this.values.includes(value);
    }

    /**
     * Returns true if range length is one day
     *
     * @return
     */
    isOneDay() {
        return this.isFull() && this.start === this.end;
    }

    /**
     * For full range: returns true if rangeStart < viewStart AND viewStart <= rangeEnd <= viewEnd.
     * For partial range: returns true for views which go after view with range limit.
     *
     * @param days   Array of days that user see on calendar
     * @return
     */
    isEndView(days: Array<{month: number; date: number; dateString: string}>): boolean {
        let firstDay = days[0].dateString;
        let lastDay = days[days.length - 1].dateString;
        if (this.isFull()) {
            return this.start < firstDay && firstDay <= this.end && this.end <= lastDay;
        } else {
            return this.values[0] < firstDay;
        }
    }

    /**
     * For full range: returns true if viewStart <= rangeStart <= viewEnd AND rangeEnd > viewEnd.
     * For partial range: returns true for views which go before view with range limit.
     *
     * @param days   Array of days that user see on calendar
     * @return
     */
    isStartView(days: Array<{month: number; date: number; dateString: string}>): boolean {
        let firstDay = days[0].dateString;
        let lastDay = days[days.length - 1].dateString;
        if (this.isFull()) {
            return firstDay <= this.start && this.start <= lastDay && this.end > lastDay;
        } else {
            return this.values[0] > lastDay;
        }
    }

    /**
     * Returns true if passed days includes in the range
     *
     * @param days   Array of days that user see on calendar
     * @return
     */
    isInRangeView(days: Array<{month: number; date: number; dateString: string}>): boolean {
        let firstDay = days[0].dateString;
        let lastDay = days[days.length - 1].dateString;
        if (this.isFull()) {
            return this.start < firstDay && lastDay < this.end;
        }
        return false;
    }

    private assignValues() {
        if (this.isFull()) {
            this.values.sort(DateRange.compareDates);
            this.start = this.values[0];
            this.end = this.values[1];
        }
    }
}
